import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  Button,
  CircularLoader,
  NoticeBox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconDownload16
} from '@dhis2/ui';
import * as XLSX from 'xlsx';
import { STAFF_ROLES_ID, POSITION_ATTRIBUTES } from '../api/constants';
import { useDataEngine } from '@dhis2/app-runtime';
import { createTEIOnly } from '../../../utils/api';


const BulkStaffImportDialog = ({
  open,
  onClose,
  onImportComplete,
  departments = [],
  existingUsers = [], // Existing users to check for duplicates
  positionsList = [] // To match staff with vacant positions
}) => {
  const [importData, setImportData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importStatus, setImportStatus] = useState({
    loading: false,
    processed: 0,
    total: 0,
    success: 0,
    failed: 0,
    results: []
  });
  const [activeStep, setActiveStep] = useState(1);
  const [availableRoles] = useState(STAFF_ROLES_ID);
  const [availableDepartments, setAvailableDepartments] = useState(departments);
  const [genderOptions, setGenderOptions] = useState([]);

  // For mapping staff to vacant positions
  const [vacantPositions, setVacantPositions] = useState([]);

  // Store existing usernames and emails to check duplicates
  const [existingUsernames, setExistingUsernames] = useState(new Set());
  const [existingEmails, setExistingEmails] = useState(new Set());

  const fileInputRef = useRef(null);
  const engine = useDataEngine();

  // Template structure
  const TEMPLATE_STRUCTURE = {
    requiredFields: ['First Name', 'Surname', 'Email', 'Phone Number', 'Gender', 'System Role', 'Department', 'Username'],
    optionalFields: ['Assign to Position']
  };


  const sendUserInvitation = async (userData) => {
    try {
      console.log('📧 [BULK] Sending invitation for:', userData.username);

      // Handle both single role (roleId) and multi-roles (userRoles array)
      const userRoles = userData.userRoles || (userData.roleId ? [userData.roleId] : []);

      const invitationData = {
        firstName: userData.firstName,
        surname: userData.surname || userData.secondName,
        email: userData.email,
        phoneNumber: userData.phoneNumber || '',
        userCredentials: {
          username: userData.username,
          userRoles: userRoles.map(roleId => ({ id: roleId }))
        },
        // CRITICAL: Both org units must be set for proper data access
        organisationUnits: userData.organisationUnits || [],
        dataViewOrganisationUnits: userData.dataViewOrganisationUnits || userData.organisationUnits || []
      };

      console.log('📤 Invitation data with org units:', invitationData);

      // Try enhanced invitation first
      try {
        const enhancedInvitationData = {
          ...invitationData,
          redirectUrl: `${window.location.origin}/set-password`,
          properties: {
            redirectUrl: `${window.location.origin}/set-password`,
            frontendUrl: window.location.origin
          }
        };
        const response = await engine.mutate({
          resource: 'users/invite',
          type: 'create',
          data: enhancedInvitationData
        });
        console.log('✅ [BULK] Invitation sent successfully for:', userData.username);
        return response;
      } catch (error) {
        // Fallback to basic invitation
        console.log('🔄 [BULK] Using basic invitation...');
        const basicResponse = await engine.mutate({
          resource: 'users/invite',
          type: 'create',
          data: invitationData
        });
        return basicResponse;
      }
    } catch (error) {
      console.error('❌ [BULK] Invitation failed for:', userData.username, error);
      throw error;
    }
  };


  // Helper function to get current user's org unit (level 1 - root)
  // Helper function to get current user's org unit (level 1 - root)
  const getCurrentUserOrgUnit = async () => {
    try {
      console.log('🔍 Getting current user org unit');
      const { me } = await engine.query({
        me: {
          resource: 'me',
          params: {
            fields: 'organisationUnits[id,name,level]'
          }
        }
      });

      if (me.organisationUnits && me.organisationUnits.length > 0) {
        // Get the HIGHEST level org unit (should be root/level 1)
        const userOrgUnit = me.organisationUnits.reduce((highest, current) => {
          return (current.level < highest.level) ? current : highest;
        });

        console.log('🎯 Found current user org unit:', {
          id: userOrgUnit.id,
          name: userOrgUnit.name,
          level: userOrgUnit.level
        });
        return {
          id: userOrgUnit.id,
          name: userOrgUnit.name,
          level: userOrgUnit.level
        };
      } else {
        console.log('⚠️ Current user has no org units assigned');
        return null;
      }
    } catch (error) {
      console.error('❌ Failed to get current user org unit:', error);
      return null;
    }
  };


  // ==================== CRITICAL: ADDINI POSITION UPDATE FUNCTION ====================
  // Same as in StaffForm - This function is missing!
  const updatePositionLink = async (positionId, staffUsername) => {
    if (!positionId || !staffUsername) {
      console.log('⚠️ Missing positionId or staffUsername');
      return null;
    }

    try {
      console.log('🔗 Updating Position TEI for staff username:', staffUsername);

      // STEP 1: Get the database User ID from the username
      console.log('🔍 Looking up database user ID for username:', staffUsername);
      const userResponse = await engine.query({
        users: {
          resource: 'users',
          params: {
            filter: `userCredentials.username:eq:${staffUsername}`,
            fields: 'id,userCredentials[username]',
            pageSize: 1
          }
        }
      });

      if (!userResponse.users.users.length) {
        throw new Error(`User ${staffUsername} not found in database`);
      }

      const dhis2User = userResponse.users.users[0];
      const dhis2UserId = dhis2User.id;

      console.log('✅ Found database user:', {
        userId: dhis2UserId,
        username: staffUsername
      });

      // STEP 2: Get the current position TEI
      const { position } = await engine.query({
        position: {
          resource: `trackedEntityInstances/${positionId}`,
          params: {
            fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
          }
        }
      });

      if (!position || !position.attributes) {
        throw new Error('Position TEI not found');
      }

      // STEP 3: Update ONLY the attributes that exist in Position TEI type
      const currentAttrs = [...position.attributes];

      const currentIncumbentAttrIndex = currentAttrs.findIndex(
        attr => attr.attribute === POSITION_ATTRIBUTES.CURRENT_INCUMBENT
      );

      if (currentIncumbentAttrIndex !== -1) {
        currentAttrs[currentIncumbentAttrIndex].value = dhis2UserId;
        console.log('📝 Updated existing incumbent attribute with database User ID:', dhis2UserId);
      } else {
        currentAttrs.push({
          attribute: POSITION_ATTRIBUTES.CURRENT_INCUMBENT,
          value: dhis2UserId
        });
        console.log('➕ Added new incumbent attribute with database User ID:', dhis2UserId);
      }

      // 2. POSITION_STATUS
      const statusAttrIndex = currentAttrs.findIndex(
        attr => attr.attribute === POSITION_ATTRIBUTES.POSITION_STATUS
      );

      if (statusAttrIndex !== -1) {
        currentAttrs[statusAttrIndex].value = "Active";
        console.log('📈 Updated position status to "Active"');
      } else {
        currentAttrs.push({
          attribute: POSITION_ATTRIBUTES.POSITION_STATUS,
          value: "Active"
        });
        console.log('➕ Added position status as "Active"');
      }

      // STEP 4: Save the updated position
      console.log('🔄 Saving updated position...');
      console.log('📦 Position update payload:', {
        trackedEntityInstance: positionId,
        orgUnit: position.orgUnit,
        attributes: currentAttrs.map(attr => ({
          attribute: attr.attribute,
          value: attr.value
        }))
      });

      const response = await engine.mutate({
        resource: `trackedEntityInstances/${positionId}`,
        type: 'update',
        data: {
          trackedEntityInstance: positionId,
          orgUnit: position.orgUnit,
          attributes: currentAttrs
        }
      });

      console.log('✅ Position updated successfully with database User ID:', dhis2UserId);
      return {
        ...response,
        userId: dhis2UserId,
        username: staffUsername
      };

    } catch (error) {
      console.error('❌ Position update failed:', error);

      // Better error handling for 409 conflicts
      if (error.message.includes('409') || error.message.includes('Conflict')) {
        console.error('🔴 CONFLICT DETAILS:');
        console.error('- You are likely trying to add attributes that don\'t belong to Position TEI type');
        console.error('- Position TEI type should only have: CURRENT_INCUMBENT, POSITION_STATUS, etc.');
        console.error('- Check your POSITION_ATTRIBUTES constants match your TEI type definition');


        throw new Error('Position TEI update conflict. Check Position TEI type attributes.');
      }

      throw new Error(`Failed to update position: ${error.message}`);
    }
  };

  // ==================== FIXED: Extract Attributes Helper ====================
  const extractAttributes = (attributes = []) => {
    const map = {};
    attributes.forEach((attr) => {
      map[attr.attribute] = attr.value;
    });
    return map;
  };


  // Helper function to get department ID from name
  const getDepartmentIdByName = (departmentName) => {
    if (!departmentName || !availableDepartments || availableDepartments.length === 0) {
      console.warn('⚠️ Cannot map department - no name or departments available');
      return '';
    }

    // Try exact match first
    const exactMatch = availableDepartments.find(
      dept => dept.name.toLowerCase() === departmentName.toLowerCase()
    );

    if (exactMatch) {
      return exactMatch.id;
    }

    // Try partial match
    const partialMatch = availableDepartments.find(
      dept => departmentName.toLowerCase().includes(dept.name.toLowerCase()) ||
        dept.name.toLowerCase().includes(departmentName.toLowerCase())
    );

    if (partialMatch) {
      console.log(`📍 Partial match for "${departmentName}": ${partialMatch.name}`);
      return partialMatch.id;
    }

    console.warn(`❌ No department match found for: "${departmentName}"`);
    return '';
  };


  // ==================== MISSING FUNCTION: Download Template ====================
  const downloadTemplate = () => {
    const headers = [
      'First Name',
      'Surname',
      'Email',
      'Phone Number',
      'Gender',
      'System Role',  // Can be comma-separated: "Data Entry Clerk, Approver"
      'Department',
      'Username',
      'Assign to Position'
    ];

    // Get available departments for examples
    const availableDepts = availableDepartments.slice(0, 5).map(d => d.name);

    // Get available roles for examples (show multi-role example)
    const availableRolesExample = availableRoles.slice(0, 5).map(r => r.label);

    // Get gender options for examples
    const genderOptionsExample = genderOptions.slice(0, 5).map(g => g.name);

    // Get ALL vacant positions
    const allVacantPositions = vacantPositions.map(pos => {
      const attrs = extractAttributes(pos.attributes);
      return {
        title: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Untitled Position',
        department: attrs[POSITION_ATTRIBUTES.DEPARTMENT] || ''
      };
    }).filter(pos => pos.title.trim() !== '');

    console.log(`📋 Preparing template with ${allVacantPositions.length} vacant positions`);

    // Example data for the template
    const exampleData = [
      {
        'First Name': 'John',
        'Surname': 'Doe',
        'Email': 'john.doe@example.com',
        'Phone Number': '+255123456789',
        'Gender': genderOptionsExample[0] || 'Male',
        'System Role': availableRolesExample[0] || 'Data Entry Clerk',  // Single role
        'Department': availableDepts[0] || 'HUMAN RESOURCES SERVICES',
        'Username': 'johndoe',
        'Assign to Position': allVacantPositions[0]?.title || ''
      },
      {
        'First Name': 'Jane',
        'Surname': 'Smith',
        'Email': 'jane.smith@example.com',
        'Phone Number': '+255987654321',
        'Gender': genderOptionsExample[1] || 'Female',
        'System Role': `${availableRolesExample[0]}, ${availableRolesExample[1]}`,  // Multi-role example
        'Department': availableDepts[1] || 'FINANCE DEPARTMENT',
        'Username': 'janesmith',
        'Assign to Position': allVacantPositions[1]?.title || ''
      }
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // ==================== SHEET 1: Template ====================
    const wsData = XLSX.utils.json_to_sheet(exampleData, { header: headers });

    // Set column widths for the template sheet
    const templateColWidths = headers.map(header => {
      switch (header) {
        case 'First Name':
        case 'Surname':
          return { wch: 15 };
        case 'Email':
          return { wch: 25 };
        case 'Phone Number':
          return { wch: 18 };
        case 'Gender':
          return { wch: 12 };
        case 'System Role':
          return { wch: 30 };  // Wider for multi-roles
        case 'Department':
          return { wch: 25 };
        case 'Username':
          return { wch: 15 };
        case 'Assign to Position':
          return { wch: 30 };
        default:
          return { wch: 15 };
      }
    });
    wsData['!cols'] = templateColWidths;
    XLSX.utils.book_append_sheet(wb, wsData, 'Template');

    // ==================== SHEET 2: Vacant Positions ====================
    // ... (keep existing code for vacant positions sheet)

    // ==================== SHEET 3: Instructions ====================
    const instructionData = [
      ['=== BULK STAFF IMPORT TEMPLATE ==='],
      [''],
      ['HOW TO USE THIS TEMPLATE:'],
      ['1. Fill in staff details in the "Template" sheet'],
      ['2. For position assignment, go to "Vacant Positions" sheet'],
      ['3. Copy EXACT "Position Title" from Column A'],
      ['4. Paste into "Assign to Position" column in Template sheet'],
      ['5. Save your completed template'],
      ['6. Upload in the bulk import dialog'],
      [''],
      ['=== MULTI-ROLE SUPPORT ==='],
      ['• System Role can accept multiple roles separated by commas'],
      ['• Example: "Data Entry Clerk, Approver, Viewer"'],
      ['• Each role must match exactly with available roles'],
      ['• At least one role is required'],
      [''],
      ['=== REQUIRED FIELDS ==='],
      ...TEMPLATE_STRUCTURE.requiredFields.map(field => [`• ${field}`]),
      [''],
      ['=== OPTIONAL FIELDS ==='],
      ...TEMPLATE_STRUCTURE.optionalFields.map(field => [`• ${field}`]),
      [''],
      ['=== IMPORTANT NOTES ==='],
      ['• Email must be unique across the entire system'],
      ['• Username must be unique and at least 4 characters'],
      ['• Phone numbers should be in international format (+255...)'],
      ['• Gender must be from available options in dropdown'],
      ['• System Role must match exactly with available roles'],
      ['• Department must match exactly with existing departments'],
      ['• Position titles must match EXACTLY from Vacant Positions sheet'],
      ['• New users will receive email invitations to set passwords'],
      ['• Position assignment only works for VACANT positions'],
      ['• Remove example rows before adding your own data'],
      ['• Maximum 1000 rows per import']
    ];

    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionData);
    // Auto-size columns for instructions sheet
    const maxColLengths = instructionData.reduce((acc, row) => {
      row.forEach((cell, colIndex) => {
        acc[colIndex] = Math.max(acc[colIndex] || 0, String(cell).length);
      });
      return acc;
    }, {});
    wsInstructions['!cols'] = Object.keys(maxColLengths).map(colIndex => ({
      wch: Math.min(Math.max(maxColLengths[colIndex] + 2, 20), 60)
    }));
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Staff_Import_Template_${timestamp}.xlsx`;
    XLSX.writeFile(wb, filename);
    console.log(`✅ Template downloaded with ${allVacantPositions.length} vacant positions`);
    console.log('📋 Sheets: Template, Vacant Positions, Instructions');
  };

  // ==================== MISSING FUNCTION: Generate Username from Email ====================
  const generateUsernameFromEmail = (email) => {
    if (!email) return '';
    const username = email.split('@')[0];
    // Remove special characters and ensure valid format
    return username.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
  };

  // ==================== MISSING FUNCTION: Get Department Names ====================
  const getDepartmentNames = () => {
    return availableDepartments.slice(0, 10).map(d => d.name).join(', ');
  };

  // ==================== MISSING FUNCTION: Get Role Names ====================
  const getRoleNames = () => {
    return availableRoles.slice(0, 5).map(r => r.label).join(', ');
  };

  // Load gender options
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const loadGenderOptions = async () => {
      try {
        const { optionSets } = await engine.query({
          optionSets: {
            resource: 'optionSets',
            params: {
              filter: 'name:eq:Gender',
              fields: 'options[id,code,name]',
              paging: 1000
            }
          }
        });

        if (isMounted && optionSets.optionSets && optionSets.optionSets[0]?.options) {
          setGenderOptions(optionSets.optionSets[0].options);
        }
      } catch (error) {
        console.error('Error loading gender options:', error);
      }
    };

    loadGenderOptions();

    return () => {
      isMounted = false;
    };
  }, [open, engine]);

  // Load existing usernames and emails for duplicate checking
  useEffect(() => {
    let isMounted = true;
    if (open && existingUsers.length > 0) {
      const usernames = new Set();
      const emails = new Set();

      existingUsers.forEach(user => {
        if (user.username) usernames.add(user.username.toLowerCase());
        if (user.email) emails.add(user.email.toLowerCase());
      });

      setExistingUsernames(usernames);
      setExistingEmails(emails);
    }

    return () => {
      isMounted = false;
    };
  }, [open, existingUsers]);

  // Load vacant positions - UPDATED with proper filtering
  useEffect(() => {
    let isMounted = true;
    if (!open) return;

    const loadVacantPositions = async () => {
      try {
        const { positions } = await engine.query({
          positions: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: 'UGGo0Ebjl0E', // Position TEI type
              ouMode: 'ACCESSIBLE',
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
              pageSize: 1000
            }
          }
        });

        const allPositions = positions?.trackedEntityInstances || [];

        // Filter for vacant positions (no incumbent or status vacant)
        const vacant = allPositions.filter(pos => {
          const attrs = extractAttributes(pos.attributes);

          // Check CURRENT_INCUMBENT for database User ID
          const existingStaffUserId = attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
          const status = (attrs[POSITION_ATTRIBUTES.POSITION_STATUS] || '').toLowerCase();

          // Position is vacant if CURRENT_INCUMBENT is empty AND status is vacant
          const hasStaff = existingStaffUserId && existingStaffUserId.trim() !== '';
          const isVacant = !hasStaff || status === 'vacant';

          return isVacant;
        });

        setVacantPositions(vacant);
        console.log(`✅ Loaded ${vacant.length} vacant positions`);

      } catch (error) {
        console.error('Error loading vacant positions:', error);
        setVacantPositions([]);
      }
    };

    if (open) {
      loadVacantPositions();
    }
    return () => {
      isMounted = false;
    };
  }, [open, engine]);

  // Load departments if not provided
  useEffect(() => {
    let isMounted = true;
    if (!open) return;

    // Add this function after the other helper functions
    const getOrgUnitIdByName = async (departmentName) => {
      if (!departmentName || !availableDepartments || availableDepartments.length === 0) {
        console.warn('⚠️ Cannot map department - no name or departments available');
        return '';
      }

      // Try exact match first
      const exactMatch = availableDepartments.find(
        dept => dept.name.toLowerCase() === departmentName.toLowerCase()
      );

      if (exactMatch) {
        return exactMatch.id;
      }

      // Try partial match
      const partialMatch = availableDepartments.find(
        dept => departmentName.toLowerCase().includes(dept.name.toLowerCase()) ||
          dept.name.toLowerCase().includes(departmentName.toLowerCase())
      );

      if (partialMatch) {
        console.log(`📍 Partial match for "${departmentName}": ${partialMatch.name}`);
        return partialMatch.id;
      }

      console.warn(`❌ No department match found for: "${departmentName}"`);
      return '';
    };

    const loadDepartments = async () => {
      try {
        console.log('🔍 Loading departments...');

        // Get org units within user's hierarchy that are level 5+
        const { organisationUnits } = await engine.query({
          organisationUnits: {
            resource: 'organisationUnits',
            params: {
              fields: 'id,name,level',
              filter: 'level:ge:5', // Level 5 and above
              withinUserHierarchy: true, // This is the key filter
              paging: 1000,
              order: 'name'
            }
          }
        });

        console.log('📊 Raw org units response:', organisationUnits);

        const depts = (organisationUnits.organisationUnits || [])
          .map(ou => ({
            id: ou.id,
            name: ou.name,
            level: ou.level
          }));

        console.log('✅ Loaded departments:', depts.length);
        console.log('📋 Department names:', depts.map(d => d.name));

        setAvailableDepartments(depts);

      } catch (error) {
        console.error('❌ Error loading departments:', error);
        setAvailableDepartments([]);
      }
    };

    if (open) {
      loadDepartments();
    }
    return () => {
      isMounted = false;
    };

  }, [open, engine]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportStatus({ ...importStatus, loading: true });

    try {
      const data = await readExcelFile(file);
      const validatedData = await validateAndProcessData(data);
      setImportData(validatedData.processed);
      setValidationErrors(validatedData.errors);
      setActiveStep(2);
    } catch (error) {
      console.error('Error processing file:', error);
      setValidationErrors([{
        row: 0,
        message: `Error reading file: ${error.message}`,
        field: 'File'
      }]);
    } finally {
      setImportStatus({ ...importStatus, loading: false });
    }
  };

  // Read and parse Excel/CSV file
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            raw: false
          });

          if (jsonData.length === 0) {
            reject(new Error('File is empty'));
            return;
          }

          // Get headers from first row
          const headers = jsonData[0].map(h => h ? h.trim() : '');

          console.log('📄 File headers:', headers);

          // Validate headers
          const expectedHeaders = TEMPLATE_STRUCTURE.requiredFields.concat(TEMPLATE_STRUCTURE.optionalFields);

          // Check if all required headers are present
          const missingHeaders = TEMPLATE_STRUCTURE.requiredFields.filter(
            header => !headers.includes(header)
          );

          if (missingHeaders.length > 0) {
            reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
            return;
          }

          // Process data rows
          const structuredData = jsonData.slice(1).map((row, index) => {
            const obj = {};
            headers.forEach((header, colIndex) => {
              if (header && row[colIndex] !== undefined) {
                obj[header] = String(row[colIndex]).trim();
              } else {
                obj[header] = '';
              }
            });
            return {
              rawData: obj,
              rowNumber: index + 2
            };
          }).filter(item => {
            // Keep rows that have at least one required field filled
            const hasRequiredField = TEMPLATE_STRUCTURE.requiredFields.some(
              field => item.rawData[field] && item.rawData[field].trim() !== ''
            );
            return hasRequiredField;
          });

          console.log('📊 Processed data:', structuredData.length, 'rows');
          resolve(structuredData);

        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };


  // In validateAndProcessData function, update this section:
  const validateAndProcessData = async (data) => {
    const errors = [];
    const processed = [];
    const existingStaffCheck = new Set();

    for (let i = 0; i < data.length; i++) {
      const { rawData, rowNumber } = data[i];
      const rowErrors = [];

      const email = rawData.Email ? rawData.Email.trim() : '';
      const phone = rawData['Phone Number'] ? rawData['Phone Number'].trim() : '';
      const gender = rawData.Gender ? rawData.Gender.trim() : '';
      const systemRole = rawData['System Role'] ? rawData['System Role'].trim() : '';
      const departmentName = rawData.Department ? rawData.Department.trim() : '';
      const username = rawData.Username ? rawData.Username.trim() : '';
      const positionTitle = rawData['Assign to Position'] ? rawData['Assign to Position'].trim() : '';

      let departmentId = '';
      let positionTei = '';

      // ==================== PARSE MULTI-ROLES ====================
      // System Role can be comma-separated: "Data Entry Clerk, Approver"
      let roleIds = [];
      if (systemRole) {
        const roleNames = systemRole.split(',').map(r => r.trim());
        roleNames.forEach(roleName => {
          const validRole = availableRoles.find(role =>
            role.label.toLowerCase() === roleName.toLowerCase()
          );
          if (validRole) {
            roleIds.push(validRole.id);
          } else {
            const validRoles = availableRoles.map(r => r.label).join(', ');
            rowErrors.push({
              field: 'System Role',
              message: `Invalid role "${roleName}". Valid roles: ${validRoles}`
            });
          }
        });

        if (roleIds.length === 0) {
          rowErrors.push({
            field: 'System Role',
            message: 'No valid roles found'
          });
        }
      }
      // ============================================================

      // Check required fields
      TEMPLATE_STRUCTURE.requiredFields.forEach(field => {
        if (!rawData[field] || rawData[field].trim() === '') {
          rowErrors.push({
            field,
            message: `${field} is required`
          });
        }
      });

      // Validate email format
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        rowErrors.push({
          field: 'Email',
          message: 'Invalid email format'
        });
      }

      // Check for duplicate email within batch
      if (email && existingStaffCheck.has(`email:${email.toLowerCase()}`)) {
        rowErrors.push({
          field: 'Email',
          message: 'Duplicate email within this batch'
        });
      } else if (email) {
        existingStaffCheck.add(`email:${email.toLowerCase()}`);
      }

      // Validate phone number
      if (phone && !/^[\d\s\-\+\(\)]+$/.test(phone)) {
        rowErrors.push({
          field: 'Phone Number',
          message: 'Invalid phone number format'
        });
      }

      // Validate gender
      if (gender && genderOptions.length > 0) {
        const validGender = genderOptions.find(g =>
          g.name.toLowerCase() === gender.toLowerCase() ||
          g.code === gender
        );
        if (!validGender) {
          const validOptions = genderOptions.map(g => g.name).join(', ');
          rowErrors.push({
            field: 'Gender',
            message: `Invalid gender. Valid options: ${validOptions}`
          });
        }
      }

      // Validate department
      if (departmentName) {
        const departmentObj = availableDepartments.find(
          dept => dept.name.toLowerCase() === departmentName.toLowerCase()
        );
        if (departmentObj) {
          departmentId = departmentObj.id;
          console.log(`✅ Found exact department match: ${departmentName} -> ${departmentId}`);
        } else {
          const partialMatch = availableDepartments.find(dept =>
            departmentName.toLowerCase().includes(dept.name.toLowerCase()) ||
            dept.name.toLowerCase().includes(departmentName.toLowerCase())
          );
          if (partialMatch) {
            departmentId = partialMatch.id;
            console.log(`📍 Found partial department match: ${departmentName} -> ${partialMatch.name} (${departmentId})`);
            rowErrors.push({
              field: 'Department',
              message: `Department name "${departmentName}" partially matched to "${partialMatch.name}"`,
              type: 'warning'
            });
          } else {
            const availableDepts = availableDepartments.slice(0, 10).map(d => d.name);
            rowErrors.push({
              field: 'Department',
              message: `Department "${departmentName}" not found. Available: ${availableDepts.join(', ')}...`,
              type: 'error'
            });
          }
        }
      }

      // Validate username
      if (username) {
        if (!/^[a-zA-Z0-9_]{4,}$/.test(username)) {
          rowErrors.push({
            field: 'Username',
            message: 'Username must be at least 4 characters (letters, numbers, underscores)'
          });
        }

        if (existingUsernames.has(username.toLowerCase())) {
          rowErrors.push({
            field: 'Username',
            message: 'Username already exists in system'
          });
        }

        if (existingStaffCheck.has(`username:${username.toLowerCase()}`)) {
          rowErrors.push({
            field: 'Username',
            message: 'Duplicate username within this batch'
          });
        } else if (username) {
          existingStaffCheck.add(`username:${username.toLowerCase()}`);
        }
      }

      // Validate position assignment
      if (positionTitle && vacantPositions.length > 0) {
        const position = vacantPositions.find(pos => {
          const attrs = extractAttributes(pos.attributes);
          return attrs[POSITION_ATTRIBUTES.POSITION_TITLE] === positionTitle;
        });
        if (position) {
          positionTei = position.trackedEntityInstance;
        } else {
          const availablePositions = vacantPositions.slice(0, 5).map(pos => {
            const attrs = extractAttributes(pos.attributes);
            return attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown';
          }).filter(Boolean);
          rowErrors.push({
            field: 'Assign to Position',
            message: `Position "${positionTitle}" not found or not vacant. Available: ${availablePositions.join(', ')}`
          });
        }
      }

      if (rowErrors.length === 0) {
        processed.push({
          ...rawData,
          rowNumber,
          username: username,
          departmentId,
          departmentName,
          positionTei,
          genderCode: genderOptions.find(g =>
            g.name.toLowerCase() === gender.toLowerCase() || g.code === gender
          )?.code || gender,
          roleId: roleIds[0] || '',  // For backward compatibility
          userRoles: roleIds,  // NEW: Multi-role support
          isValid: true
        });
      } else {
        const criticalErrors = rowErrors.filter(e => e.type !== 'warning');
        if (criticalErrors.length > 0) {
          errors.push({
            rowNumber,
            errors: criticalErrors,
            data: rawData,
            warnings: rowErrors.filter(e => e.type === 'warning')
          });
        } else {
          processed.push({
            ...rawData,
            rowNumber,
            username: username,
            departmentId,
            departmentName,
            positionTei,
            genderCode: genderOptions.find(g =>
              g.name.toLowerCase() === gender.toLowerCase() || g.code === gender
            )?.code || gender,
            roleId: roleIds[0] || '',
            userRoles: roleIds,
            isValid: true,
            warnings: rowErrors.filter(e => e.type === 'warning')
          });
        }
      }
    }

    console.log('📊 Processed data summary:', {
      total: processed.length,
      withDepartment: processed.filter(p => p.departmentId).length,
      departments: processed.map(p => ({
        name: p.Department,
        id: p.departmentId
      }))
    });
    return { processed, errors };
  };

  // Start import process - UPDATED with proper error handling
  const handleStartImport = async () => {
    if (importData.length === 0) return;

    // First, log all department mappings for debugging
    console.log('🔍 PRE-IMPORT DEPARTMENT MAPPINGS:');
    importData.forEach((staff, index) => {
      console.log(`Row ${staff.rowNumber}:`, {
        name: staff['First Name'] + ' ' + staff['Surname'],
        departmentFromFile: staff.Department,
        departmentId: staff.departmentId,
        departmentName: staff.departmentName
      });
    });

    setImportStatus({
      loading: true,
      processed: 0,
      total: importData.length,
      success: 0,
      failed: 0,
      results: []
    });
    setActiveStep(3);

    const results = [];

    try {
      for (let i = 0; i < importData.length; i++) {
        const staffData = importData[i];

        try {
          const result = await createStaff(staffData);
          results.push({
            success: true,
            data: staffData,
            username: result.username
          });
          setImportStatus(prev => ({
            ...prev,
            processed: i + 1,
            success: prev.success + 1,
            results: [...prev.results, { ...staffData, success: true }]
          }));
        } catch (error) {
          console.error(`❌ Failed to create staff "${staffData['First Name']} ${staffData['Surname']}":`, error.message);
          results.push({
            success: false,
            error: error.message,
            rowData: staffData
          });
          setImportStatus(prev => ({
            ...prev,
            processed: i + 1,
            failed: prev.failed + 1,
            results: [...prev.results, { ...staffData, success: false, error: error.message }]
          }));
        }

        // Delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 800));
      }

      setImportStatus(prev => ({ ...prev, loading: false }));

      // Trigger refresh in parent
      if (onImportComplete) {
        onImportComplete({
          success: importStatus.success,
          failed: importStatus.failed,
          total: importData.length,
          results
        });
      }
    } catch (error) {
      console.error('💥 Unexpected error during bulk import:', error);
      setImportStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const debugDepartmentMapping = () => {
    console.log('🔍 DEBUG: Department Mapping Status');
    console.log('📊 Available departments:', availableDepartments.length);
    console.log('📋 First 10 departments:', availableDepartments.slice(0, 10).map(d => d.name));

    if (importData.length > 0) {
      console.log('📝 Sample data department mapping:');
      importData.slice(0, 5).forEach((staff, i) => {
        console.log(`Row ${i + 1}:`, {
          name: `${staff['First Name']} ${staff['Surname']}`,
          departmentFromFile: staff['Department'],
          mappedDepartmentId: staff.departmentId,
          hasOrgUnitId: !!staff.departmentId
        });
      });
    }
  };


  // Create individual staff member 
  const createStaff = async (staffData) => {
    console.log('👤 [BULK] Creating staff:', staffData['First Name'], staffData['Surname']);
    try {
      // Step 0: Get department ID and validate level
      let departmentName = staffData['Department'] || '';
      let departmentId = staffData.departmentId || '';

      if (departmentName && !departmentId) {
        departmentId = getDepartmentIdByName(departmentName);
        if (!departmentId) {
          throw new Error(`Department "${departmentName}" not found in system`);
        }
      }

      // Verify department is level 5 or higher
      let deptLevel = 5;
      try {
        const { orgUnit } = await engine.query({
          orgUnit: {
            resource: `organisationUnits/${departmentId}`,
            params: { fields: 'name,level' }
          }
        });
        departmentName = orgUnit.name;
        deptLevel = orgUnit.level;

        if (deptLevel < 5) {
          console.warn(`⚠️ Department "${departmentName}" is level ${deptLevel}. Looking for sub-units...`);
          const { organisationUnits } = await engine.query({
            organisationUnits: {
              resource: 'organisationUnits',
              params: {
                filter: `level:ge:5`,
                withinUserHierarchy: true,
                fields: 'id,name,level',
                filter: `path:like:${departmentId}`,
                paging: 1
              }
            }
          });

          if (organisationUnits.organisationUnits && organisationUnits.organisationUnits[0]) {
            const subUnit = organisationUnits.organisationUnits[0];
            departmentId = subUnit.id;
            departmentName = subUnit.name;
            deptLevel = subUnit.level;
            console.log(`✅ Using sub-unit: ${departmentName} (level ${deptLevel})`);
          }
        }
      } catch (ouError) {
        console.warn('⚠️ Could not verify department level:', ouError);
      }

      console.log('📋 Final department assignment:', {
        name: departmentName,
        id: departmentId,
        level: deptLevel
      });

      // ==================== CRITICAL: Get current user's org unit ====================
      const currentUserOrgUnit = await getCurrentUserOrgUnit();
      console.log('🏢 Current user org unit:', currentUserOrgUnit);

      // ==================== Prepare org units array ====================
      const organisationUnits = [];
      const dataViewOrganisationUnits = [];

      // Add selected department org unit
      if (departmentId) {
        organisationUnits.push({ id: departmentId });
        dataViewOrganisationUnits.push({ id: departmentId });
      }

      // Add current user's org unit if different from department
      if (currentUserOrgUnit && currentUserOrgUnit.id !== departmentId) {
        console.log('➕ Adding current user org unit:', currentUserOrgUnit.name);
        organisationUnits.push({ id: currentUserOrgUnit.id });
        dataViewOrganisationUnits.push({ id: currentUserOrgUnit.id });
      }

      console.log('📦 Org units to assign:', {
        total: organisationUnits.length,
        organisationUnits: organisationUnits.map(ou => ou.id),
        dataViewOrganisationUnits: dataViewOrganisationUnits.map(ou => ou.id)
      });

      // ==================== STEP 1: Send User Invitation with BOTH org units ====================
      const userData = {
        firstName: staffData['First Name'],
        surname: staffData['Surname'],
        email: staffData['Email'],
        username: staffData.username,
        phoneNumber: staffData['Phone Number'],
        roleId: staffData.roleId,
        userRoles: staffData.userRoles || (staffData.roleId ? [staffData.roleId] : []),
        organisationUnits: organisationUnits,
        dataViewOrganisationUnits: dataViewOrganisationUnits
      };

      console.log('📧 Sending invitation for:', staffData.username);
      let invitationResult;
      try {
        invitationResult = await sendUserInvitation(userData);
        console.log('✅ Invitation sent successfully for:', staffData.username);
      } catch (inviteError) {
        if (inviteError.message.includes('409') || inviteError.message.includes('Username is already taken')) {
          throw new Error(`Username "${staffData.username}" is already taken`);
        }
        throw inviteError;
      }

      // ==================== STEP 2: Create TEI using createTEIOnly utility ====================
      try {
        console.log('📝 Creating TEI record...');

        const staffTeiData = {
          firstName: staffData['First Name'],
          secondName: staffData['Surname'],
          surname: staffData['Surname'],
          username: staffData.username,
          email: staffData['Email'],
          phoneNumber: staffData['Phone Number'],
          userRole: staffData.roleId || (staffData.userRoles && staffData.userRoles[0]),
          userRoles: staffData.userRoles || [],
          orgUnit: departmentId,
          orgUnitName: staffData.departmentName,
          gender: staffData.genderCode,
          department: staffData['Department']
        };

        const teiResult = await createTEIOnly(engine, staffTeiData, true);
        console.log('✅ TEI created successfully:', teiResult);
      } catch (teiError) {
        console.error(`❌ TEI creation failed for ${staffData.username}:`, teiError);
        console.warn('⚠️ Continuing without TEI - user account was created');
      }

      // ==================== STEP 3: Assign to position if specified ====================
      if (staffData.positionTei) {
        try {
          console.log(`🔗 [BULK] Attempting position assignment for ${staffData.username} to ${staffData.positionTei}`);
          const positionResult = await updatePositionLink(staffData.positionTei, staffData.username);
          console.log(`✅ [BULK] Position assigned for ${staffData.username}`);
        } catch (positionError) {
          console.warn(`⚠️ [BULK] Position assignment failed for ${staffData.username}:`, positionError.message);
          // Non-critical error - continue
        }
      }

      // ==================== STEP 4: Verify org unit assignment ====================
      try {
        console.log('🔍 Verifying user organisation units...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        const { users } = await engine.query({
          users: {
            resource: 'users',
            params: {
              filter: `userCredentials.username:eq:${staffData.username}`,
              fields: 'id,organisationUnits[id,name,level]'
            }
          }
        });

        if (users.users && users.users[0]) {
          const userId = users.users[0].id;
          const currentOrgUnits = users.users[0].organisationUnits || [];

          console.log(`📌 Current org units:`, currentOrgUnits.map(ou => `${ou.name} (level ${ou.level})`));

          // Check if we have the correct org units assigned
          const hasDepartment = currentOrgUnits.some(ou => ou.id === departmentId);
          const hasCurrentUserOrgUnit = currentUserOrgUnit
            ? currentOrgUnits.some(ou => ou.id === currentUserOrgUnit.id)
            : true; // If no current user org unit, skip this check

          if (!hasDepartment || !hasCurrentUserOrgUnit) {
            console.log(`📝 Setting correct org units...`);

            // Build the correct org units array
            const correctOrgUnits = [...organisationUnits];

            await engine.mutate({
              resource: 'users',
              type: 'update',
              id: userId,
              data: {
                organisationUnits: correctOrgUnits,
                dataViewOrganisationUnits: correctOrgUnits
              }
            });

            console.log('✅ User organisation units updated to correct org units');
          } else {
            console.log('✅ User already has the correct org units assigned');
          }
        }
      } catch (updateError) {
        console.error('❌ Failed to verify/update user org units:', updateError.message);
        // Continue - user was created
      }

      return {
        username: staffData.username,
        success: true,
        message: 'Staff created successfully',
        department: departmentName,
        departmentId: departmentId,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error(`❌ [BULK] Error creating staff "${staffData['First Name']} ${staffData['Surname']}":`, error);
      let errorMessage = error.message;

      if (error.message.includes('409') || error.message.includes('Conflict') || error.message.includes('Username is already taken')) {
        errorMessage = `Username ${staffData.username} is already taken.`;
      } else if (error.message.includes('organisationUnits')) {
        errorMessage = `Department issue: ${error.message}`;
      }

      throw new Error(`Failed to create staff: ${errorMessage}`);
    }
  };

  // Reset dialog
  const handleReset = () => {
    setImportData([]);
    setValidationErrors([]);
    setImportStatus({
      loading: false,
      processed: 0,
      total: 0,
      success: 0,
      failed: 0,
      results: []
    });
    setActiveStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Error export function
  const exportErrors = () => {
    const errorCSV = validationErrors.map(err => ({
      'Row': err.rowNumber,
      'First Name': err.data['First Name'] || '',
      'Surname': err.data['Surname'] || '',
      'Email': err.data['Email'] || '',
      'Error Field': err.errors.map(e => e.field).join('; '),
      'Error Message': err.errors.map(e => e.message).join('; ')
    }));

    const ws = XLSX.utils.json_to_sheet(errorCSV);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, 'Staff_Import_Errors.xlsx');
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} large>
      <ModalTitle>Bulk Staff Import</ModalTitle>

      <ModalContent>
        {/* Step Indicator */}
        <div style={{
          display: 'flex',
          marginBottom: '24px',
          justifyContent: 'center',
          gap: '8px'
        }}>
          {[1, 2, 3].map(step => (
            <div
              key={step}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: activeStep >= step ? 1 : 0.5
              }}
            >
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: activeStep === step ? '#2C6693' : '#e0e0e0',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                marginBottom: '4px'
              }}>
                {step}
              </div>
              <span style={{ fontSize: '12px' }}>
                {step === 1 && 'Upload'}
                {step === 2 && 'Validate'}
                {step === 3 && 'Import'}
              </span>
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {activeStep === 1 && (
          <div style={{ padding: '20px' }}>
            <NoticeBox title="Bulk Staff Import Instructions" style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                <p><strong>How to use bulk import:</strong></p>
                <ol style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>Download the template (This includes all vacant positions)</li>
                  <li>Fill in staff details in the "Template" sheet</li>
                  <li>For position assignment, copy EXACT titles from "Vacant Positions" sheet</li>
                  <li>Save and upload the completed file</li>
                </ol>

                {vacantPositions.length > 0 && (
                  <div style={{ marginTop: '12px', padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>📋 Vacant Positions Available:</strong> {vacantPositions.length}
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      All vacant positions will be included in the template download
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <div>
                    <strong>Departments:</strong> {availableDepartments.length}
                  </div>
                  <div>
                    <strong>System Roles:</strong> {availableRoles.length}
                  </div>
                  <div>
                    <strong>Gender Options:</strong> {genderOptions.length}
                  </div>
                </div>
              </div>
            </NoticeBox>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
              gap: '14px'
            }}>
              <button
                onClick={downloadTemplate}
                style={{
                  backgroundColor: "#1f7706ff",
                  color: "#ffffffff",
                  border: 'none',
                  boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                  borderRadius: '5px',
                  width: '220px',
                  height: '40px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '10px'
                }}
              >
                <IconDownload16 />
                Download Template
              </button>
            </div>

            <div style={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              padding: '40px',
              margin: '20px 0',
              textAlign: 'center'
            }}>
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              {importStatus.loading ? (
                <div>
                  <CircularLoader small />
                  <p style={{ marginTop: '10px' }}>Processing file...</p>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                    <strong>Step 2: Upload your completed template</strong>
                  </p>
                  <Button
                    onClick={() => fileInputRef.current.click()}
                    primary
                    large
                  >
                    Browse & Upload
                  </Button>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Supports .xlsx, .xls, .csv files
                  </p>
                  <p style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    Make sure you've filled the template and saved it first
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Validate */}
        {activeStep === 2 && (
          <div>
            <NoticeBox
              title={`Validation Results: ${importData.length} valid, ${validationErrors.length} with errors`}
              style={{ marginBottom: '20px' }}
              valid={validationErrors.length === 0}
              warning={validationErrors.length > 0}
            >
              {validationErrors.length === 0 ? (
                'All data is valid. Ready to import.'
              ) : (
                'Please fix errors in your file and re-upload, or proceed with valid staff only.'
              )}
            </NoticeBox>

            {validationErrors.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ color: '#d32f2f' }}>
                    Errors Found ({validationErrors.length} rows)
                  </h4>
                  <Button
                    small
                    onClick={exportErrors}
                  >
                    Export Errors
                  </Button>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Errors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationErrors.slice(0, 50).map((error, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{error.rowNumber}</TableCell>
                          <TableCell>{`${error.data['First Name'] || ''} ${error.data['Surname'] || ''}`}</TableCell>
                          <TableCell>{error.data['Email'] || 'N/A'}</TableCell>
                          <TableCell style={{ color: '#d32f2f', fontSize: '12px' }}>
                            {error.errors.map((e, i) => (
                              <div key={i} style={{ marginBottom: '4px' }}>
                                <strong>{e.field}:</strong> {e.message}
                              </div>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {validationErrors.length > 50 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Showing first 50 of {validationErrors.length} errors
                  </p>
                )}
              </div>
            )}

            {importData.length > 0 && (
              <div>
                <h4>Valid Staff ({importData.length}):</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importData.slice(0, 10).map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {item['First Name']} {item['Surname']}
                          </TableCell>
                          <TableCell>{item['Email']}</TableCell>
                          <TableCell>
                            <code style={{ fontSize: '11px' }}>{item.username}</code>
                          </TableCell>
                          <TableCell>{item['System Role']}</TableCell>
                          <TableCell>{item['Department']}</TableCell>
                          <TableCell>
                            {item['Assign to Position'] ? (
                              <Chip dense color="primary">
                                {item['Assign to Position']}
                              </Chip>
                            ) : (
                              <span style={{ color: '#999', fontSize: '11px' }}>None</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {importData.length > 10 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Showing first 10 of {importData.length} staff
                  </p>
                )}

                {/* Summary stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '10px',
                  marginTop: '20px',
                  fontSize: '12px'
                }}>
                  <div style={{
                    background: '#e8f5e9',
                    padding: '10px',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{importData.length}</div>
                    <div>Total Staff</div>
                  </div>
                  <div style={{
                    background: '#e3f2fd',
                    padding: '10px',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {importData.filter(s => s['Assign to Position']).length}
                    </div>
                    <div>With Position</div>
                  </div>
                  <div style={{
                    background: '#fff8e1',
                    padding: '10px',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {importData.filter(s => s['Username'] && s['Username'] === generateUsernameFromEmail(s['Email'])).length}
                    </div>
                    <div>Auto-generated Username</div>
                  </div>
                  <div style={{
                    background: '#f5f5f5',
                    padding: '10px',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      {new Set(importData.map(s => s['System Role'])).size}
                    </div>
                    <div>Unique Roles</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Import Progress */}
        {activeStep === 3 && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            {importStatus.loading ? (
              <div>
                <CircularLoader small />
                <h3 style={{ marginTop: '20px' }}>Importing Staff...</h3>
                <p style={{ marginTop: '10px' }}>
                  Processed {importStatus.processed} of {importStatus.total} staff
                  ({Math.round((importStatus.processed / importStatus.total) * 100)}%)
                </p>
                <div style={{
                  width: '100%',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  margin: '30px 0 20px',
                  height: '20px'
                }}>
                  <div
                    style={{
                      width: `${(importStatus.processed / importStatus.total) * 100}%`,
                      height: '100%',
                      backgroundColor: '#2C6693',
                      borderRadius: '4px',
                      transition: 'width 0.5s'
                    }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '40px',
                  marginTop: '20px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>
                      ✓ {importStatus.success}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Success</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f44336' }}>
                      ✗ {importStatus.failed}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
                  New users will receive email invitations to complete their registration
                </p>
              </div>
            ) : (
              <div>
                <div style={{
                  fontSize: '72px',
                  color: importStatus.failed === 0 ? '#4caf50' : '#ff9800',
                  marginBottom: '20px'
                }}>
                  {importStatus.failed === 0 ? '✓' : '⚠'}
                </div>

                <NoticeBox
                  title={importStatus.failed === 0 ? "Import Complete!" : "Import Completed with Issues"}
                  valid={importStatus.failed === 0}
                  warning={importStatus.failed > 0}
                >
                  <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                    Successfully imported <strong>{importStatus.success}</strong> staff.
                    {importStatus.failed > 0 && (
                      <span> <strong>{importStatus.failed}</strong> staff failed to import.</span>
                    )}
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    New users have been sent email invitations to complete their registration.
                  </p>
                  {importStatus.failed > 0 && (
                    <p style={{ fontSize: '14px', color: '#666' }}>
                      Check the error log for details on failed imports.
                    </p>
                  )}
                </NoticeBox>

                <div style={{ marginTop: '30px' }}>
                  <Button
                    onClick={() => {
                      handleReset();
                      onClose();
                    }}
                    primary
                  >
                    Close
                  </Button>
                  {importStatus.failed > 0 && (
                    <Button
                      onClick={() => {
                        // Option to retry failed imports
                        alert('Retry failed imports feature coming soon');
                      }}
                      style={{ marginLeft: '10px' }}
                    >
                      Retry Failed
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </ModalContent>

      <ModalActions>
        {/* Add container with spacing */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            primary
            onClick={activeStep === 1 ? onClose : handleReset}
            disabled={importStatus.loading} // Disable during import
          >
            {activeStep === 1 ? 'Cancel' : 'Start Over'}
          </Button>

          {activeStep === 2 && importData.length > 0 && (
            <Button
              primary
              onClick={handleStartImport}
              disabled={importStatus.loading}
            >
              Import {importData.length} Staff
            </Button>
          )}
        </div>
      </ModalActions>
    </Modal>
  );
};

export default BulkStaffImportDialog;